import { useState } from 'react';
import { CheckCircle2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatDateTime } from '@/lib/format';

export interface DevicePairingResult {
  deviceName: string;
  pairingCode: string;
  expiresAt: string;
}

export interface DevicePairingModalProps {
  open: boolean;
  pairing: DevicePairingResult | null;
  onClose: () => void;
}

/**
 * Shows the one-time 8-character code after createDevice or
 * regenerateDevicePairingCode. The manager reads this out to pair a device.
 */
export function DevicePairingModal({
  open,
  pairing,
  onClose,
}: DevicePairingModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pair device"
      description={
        pairing
          ? `Enter this code on ${pairing.deviceName}. It expires at ${formatDateTime(pairing.expiresAt)}.`
          : 'No pairing code available.'
      }
      size="md"
      footer={
        <Button id="admin.devices.pairingDone" onClick={onClose} variant="secondary">
          Done
        </Button>
      }
    >
      {pairing ? (
        <PairingCodeBody key={pairing.pairingCode} pairing={pairing} />
      ) : (
        <p className="text-sm text-gray-500">No pairing code has been generated yet.</p>
      )}
    </Modal>
  );
}

function PairingCodeBody({ pairing }: { pairing: DevicePairingResult }) {
  const [copied, setCopied] = useState(false);
  return (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Open the Ferry POS app on the device, enter the code below, and tap Pair.
          </p>
          <div className="rounded-xl border border-brand-200 bg-brand-50 px-6 py-8 text-center dark:border-brand-800 dark:bg-brand-950/40">
            <p
              id="admin.devices.pairingCode"
              className="font-mono text-4xl font-bold tracking-[0.35em] text-gray-900 dark:text-white"
            >
              {pairing.pairingCode}
            </p>
          </div>
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              leftIcon={
                copied ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )
              }
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(pairing.pairingCode);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch {
                  // ignore — clipboard is optional
                }
              }}
            >
              {copied ? 'Copied' : 'Copy code'}
            </Button>
          </div>
        </div>
  );
}
